// Ed25519 keypair generation in the browser.
//
// The private key is generated here, offered to the user as a download, and then
// discarded. It is never sent to the API, never written to localStorage, and
// never logged -- the only thing that leaves this file is the public half. That
// is the whole reason this module exists rather than a server-side endpoint: a
// server that generates keypairs has, however briefly, held every partner's
// private key, and there is no way to prove afterwards that it did not keep one.
//
// The cost is that both OpenSSH formats have to be written by hand, because
// WebCrypto speaks SPKI and PKCS#8 while ssh-keygen and every SFTP client speak
// neither. The encoders below are the OpenSSH wire format, which is simple: a
// sequence of length-prefixed byte strings.

export interface GeneratedKeyPair {
  // "ssh-ed25519 AAAAC3... comment" — the only part that gets sent anywhere.
  publicKey: string;
  // OpenSSH PEM. Written to a file by the user and used as `ssh -i`.
  privateKey: string;
  // SHA256:... as ssh-keygen -l prints it, so the user can confirm the key on
  // their machine is the one the portal recorded.
  fingerprint: string;
}

export class UnsupportedBrowserError extends Error {}

export async function generateEd25519KeyPair(comment: string): Promise<GeneratedKeyPair> {
  const pair = await generate();

  // 'raw' gives the 32-byte public key directly. Some implementations only
  // support 'spki', where the key is the final 32 bytes of the DER structure --
  // the Ed25519 SPKI header is a fixed 12 bytes, so this is exact rather than a
  // guess.
  const publicBytes = await exportRawPublicKey(pair.publicKey);
  // PKCS#8 for Ed25519 wraps the 32-byte seed in a fixed 16-byte header, so the
  // seed is likewise the last 32 bytes.
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey('pkcs8', pair.privateKey));
  const seed = pkcs8.slice(pkcs8.length - 32);

  const publicBlob = concat(sshString(encodeUtf8(KEY_TYPE)), sshString(publicBytes));

  return {
    publicKey: `${KEY_TYPE} ${base64(publicBlob)} ${comment}`.trim(),
    privateKey: encodeOpenSshPrivateKey(publicBlob, publicBytes, seed, comment),
    fingerprint: await fingerprintOf(publicBlob),
  };
}

const KEY_TYPE = 'ssh-ed25519';

const generate = async (): Promise<CryptoKeyPair> => {
  try {
    return (await crypto.subtle.generateKey({ name: 'Ed25519' }, true, [
      'sign',
      'verify',
    ])) as CryptoKeyPair;
  } catch {
    // Ed25519 in WebCrypto is recent. Rather than silently falling back to RSA
    // -- which would mean quietly issuing a weaker key than the one that was
    // asked for -- this says so, and the caller offers the paste-a-key path.
    throw new UnsupportedBrowserError(
      'This browser cannot generate Ed25519 keys. Update it, or generate the key ' +
        'yourself with "ssh-keygen -t ed25519" and paste the public half.'
    );
  }
};

const exportRawPublicKey = async (key: CryptoKey): Promise<Uint8Array> => {
  try {
    return new Uint8Array(await crypto.subtle.exportKey('raw', key));
  } catch {
    const spki = new Uint8Array(await crypto.subtle.exportKey('spki', key));
    return spki.slice(spki.length - 32);
  }
};

// The openssh-key-v1 container, unencrypted.
//
// Unencrypted because a passphrase entered in a web page and applied by
// hand-written bcrypt-pbkdf here would be worse than no passphrase: it looks
// like protection while depending on code with no cryptographic review. The
// user is told to add one themselves with `ssh-keygen -p`, which is the tool
// that does it properly.
const encodeOpenSshPrivateKey = (
  publicBlob: Uint8Array,
  publicBytes: Uint8Array,
  seed: Uint8Array,
  comment: string
): string => {
  // Two identical random integers. On decrypt, OpenSSH checks they still match
  // as a cheap "was the passphrase right" test; with no cipher it is just a
  // formality, but the format requires it.
  const checkInt = crypto.getRandomValues(new Uint8Array(4));

  let privateSection = concat(
    checkInt,
    checkInt,
    sshString(encodeUtf8(KEY_TYPE)),
    sshString(publicBytes),
    // OpenSSH stores the Ed25519 private key as seed || public key.
    sshString(concat(seed, publicBytes)),
    sshString(encodeUtf8(comment))
  );
  // Padded to the cipher block size with 1, 2, 3... The "none" cipher still uses
  // a block size of 8 here.
  const padding: number[] = [];
  for (let i = 1; (privateSection.length + padding.length) % 8 !== 0; i++) {
    padding.push(i);
  }
  privateSection = concat(privateSection, new Uint8Array(padding));

  const body = concat(
    encodeUtf8('openssh-key-v1\0'),
    sshString(encodeUtf8('none')), // cipher
    sshString(encodeUtf8('none')), // kdf
    sshString(new Uint8Array(0)), // kdf options
    uint32(1), // one key in this file
    sshString(publicBlob),
    sshString(privateSection)
  );

  const lines = base64(body).match(/.{1,70}/g) ?? [];
  return [
    '-----BEGIN OPENSSH PRIVATE KEY-----',
    ...lines,
    '-----END OPENSSH PRIVATE KEY-----',
    '',
  ].join('\n');
};

// Same input and encoding as `ssh-keygen -lf`: SHA-256 of the public key blob,
// base64 with the padding stripped.
export const fingerprintOf = async (publicBlob: Uint8Array): Promise<string> => {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', publicBlob));
  return `SHA256:${base64(digest).replace(/=+$/, '')}`;
};

// Recomputes the fingerprint of a key the user pasted, so the same value is
// shown whether the portal generated the key or not.
export const fingerprintOfPublicKey = async (publicKey: string): Promise<string | undefined> => {
  const encoded = publicKey.trim().split(/\s+/)[1];
  if (!encoded) return undefined;
  try {
    const bytes = Uint8Array.from(atob(encoded), (char) => char.charCodeAt(0));
    return await fingerprintOf(bytes);
  } catch {
    return undefined;
  }
};

// ---- OpenSSH wire-format primitives ------------------------------------------

const uint32 = (value: number): Uint8Array =>
  new Uint8Array([(value >>> 24) & 0xff, (value >>> 16) & 0xff, (value >>> 8) & 0xff, value & 0xff]);

// Every field in these formats is a 32-bit big-endian length followed by that
// many bytes.
const sshString = (bytes: Uint8Array): Uint8Array => concat(uint32(bytes.length), bytes);

const concat = (...parts: Uint8Array[]): Uint8Array => {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
};

const encodeUtf8 = (value: string): Uint8Array => new TextEncoder().encode(value);

const base64 = (bytes: Uint8Array): string => {
  let binary = '';
  // In chunks, because a spread of a large array overflows the argument limit.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};
